import { useState, useMemo } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Chip,
} from '@heroui/react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { useProducts } from '@/api/products';
import { useFrameTemplates } from '@/api/frame-templates';
import { useCreateLineItem } from '@/api/line-items';
import { toTemplatePayload } from '@/utils/line-item-template';

interface ProductPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  templateMode?: boolean;
}

type ProductSource = 'cabinet' | 'frame';

interface UnifiedProduct {
  id: string;
  source: ProductSource;
  code: string;
  name: string;
  type: string;
  role: string;
  width: number;
  height: number;
  depth: number;
}

const FACE_TYPES = new Set(['Panel', 'Filler', 'panel', 'filler']);

function getRoleForFrame(frameConfig: string): string {
  try {
    const config = JSON.parse(frameConfig || '{}');
    if (config.frameRole === 'face_component') return 'Face Component';
  } catch { /* ignore */ }
  return 'Assembly';
}

export default function ProductPickerModal({ isOpen, onClose, areaId, templateMode = false }: ProductPickerModalProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Map<string, number>>(new Map());
  const [isAdding, setIsAdding] = useState(false);

  const { data: cabinets = [], isLoading: loadingCabinets } = useProducts();
  const { data: frames = [], isLoading: loadingFrames } = useFrameTemplates();
  const createLineItem = useCreateLineItem();

  const isLoading = loadingCabinets || loadingFrames;

  // Merge cabinet templates + frame templates into unified list
  const allProducts = useMemo<UnifiedProduct[]>(() => {
    const cabinetRows: UnifiedProduct[] = (cabinets as any[]).map((c) => ({
      id: c.id,
      source: 'cabinet' as const,
      code: c.cabinet_code || c.code || '',
      name: c.name,
      type: c.cabinet_type || '',
      role: FACE_TYPES.has(c.cabinet_type) ? 'Face Component' : 'Assembly',
      width: c.default_width ?? 0,
      height: c.default_height ?? 0,
      depth: c.default_depth ?? 0,
    }));

    const frameRows: UnifiedProduct[] = (frames as any[]).map((f) => ({
      id: f.id,
      source: 'frame' as const,
      code: f.code || '',
      name: f.name,
      type: f.frame_type || 'Frame',
      role: getRoleForFrame(f.frame_config || '{}'),
      width: f.default_width ?? 0,
      height: f.default_height ?? 0,
      depth: f.default_depth ?? 0,
    }));

    return [...cabinetRows, ...frameRows].sort((a, b) => a.code.localeCompare(b.code));
  }, [cabinets, frames]);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return allProducts;
    const q = search.toLowerCase();
    return allProducts.filter((p) =>
      p.code.toLowerCase().includes(q) ||
      p.name.toLowerCase().includes(q) ||
      p.type.toLowerCase().includes(q) ||
      p.role.toLowerCase().includes(q)
    );
  }, [allProducts, search]);

  const toggleProduct = (id: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, 1);
      return next;
    });
  };

  const setQuantity = (id: string, qty: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(id, Math.max(1, qty));
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredProducts.length) {
      setSelected(new Map());
    } else {
      const next = new Map<string, number>();
      filteredProducts.forEach((p) => next.set(p.id, selected.get(p.id) ?? 1));
      setSelected(next);
    }
  };

  const handleAdd = async () => {
    if (selected.size === 0) {
      toast.error('No products selected');
      return;
    }

    setIsAdding(true);
    let successCount = 0;

    for (const [productId, quantity] of selected) {
      const product = allProducts.find((p) => p.id === productId);
      if (!product) continue;

      for (let i = 0; i < quantity; i++) {
        try {
          let payload: any;
          if (templateMode) {
            // Template: copy denormalized columns only — no template/frame/area relation.
            payload = toTemplatePayload({
              description: product.name || product.code || 'Template',
              quantity: 1,
              sort_order: 0,
              cabinet_code: product.code || null,
              width: product.width || null,
              height: product.height || null,
              depth: product.depth || null,
            });
          } else {
            payload = {
              area_id: areaId,
              description: '',
              quantity: 1,
              sort_order: 0,
            };
            if (product.source === 'cabinet') {
              payload.template_id = product.id;
            } else {
              payload.frame_template_id = product.id;
              payload.description = product.name;
              payload.width = product.width;
              payload.height = product.height;
              payload.depth = product.depth;
              payload.cabinet_code = product.code;
            }
          }

          await createLineItem.mutateAsync(payload);
          successCount++;
        } catch {
          toast.error(`Failed to add ${product.name}`);
        }
      }
    }

    if (successCount > 0) {
      toast.success(
        templateMode
          ? `Created ${successCount} template${successCount > 1 ? 's' : ''}`
          : `Added ${successCount} item${successCount > 1 ? 's' : ''}`,
      );
    }
    setSelected(new Map());
    setSearch('');
    setIsAdding(false);
    onClose();
  };

  const handleClose = () => {
    setSelected(new Map());
    setSearch('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-[var(--bg-surface)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {templateMode ? 'Create Templates from Products' : 'Add Products to Area'}
          </span>
        </ModalHeader>
        <ModalBody className="py-4">
          <Input
            placeholder="Search by code, name, or type..."
            value={search}
            onValueChange={setSearch}
            size="sm"
            startContent={<Search size={14} style={{ color: 'var(--text-muted)' }} />}
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            className="mb-3"
          />

          <div className="flex items-center justify-between mb-2">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} available
            </span>
            <span className="text-xs font-medium" style={{ color: 'var(--accent)' }}>
              {selected.size} selected
            </span>
          </div>

          <div className="rounded-lg border overflow-hidden max-h-[400px] overflow-y-auto" style={{ borderColor: 'var(--border)' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-0" style={{ backgroundColor: 'var(--bg-panel)' }}>
                <tr>
                  <th className="px-3 py-2 text-center w-[32px]">
                    <input
                      type="checkbox"
                      checked={selected.size > 0 && selected.size === filteredProducts.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--text-secondary)' }}>Code</th>
                  <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--text-secondary)' }}>Name</th>
                  <th className="px-3 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>Type</th>
                  <th className="px-3 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>Role</th>
                  <th className="px-3 py-2 text-center font-medium" style={{ color: 'var(--text-secondary)' }}>W × H × D</th>
                  <th className="px-3 py-2 text-center font-medium w-[70px]" style={{ color: 'var(--text-secondary)' }}>Qty</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                      Loading products...
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                      {search ? 'No matching products' : 'No products in library'}
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => {
                    const isSelected = selected.has(product.id);
                    return (
                      <tr
                        key={`${product.source}-${product.id}`}
                        className="border-t transition-colors cursor-pointer hover:bg-[var(--bg-hover)]"
                        style={{ borderColor: 'var(--border)' }}
                        onClick={() => toggleProduct(product.id)}
                      >
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleProduct(product.id)}
                          />
                        </td>
                        <td className="px-3 py-2 font-mono" style={{ color: 'var(--accent)' }}>
                          {product.code || '-'}
                        </td>
                        <td className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>
                          {product.name}
                        </td>
                        <td className="px-3 py-2 text-center" style={{ color: 'var(--text-muted)' }}>
                          {product.type || '-'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Chip
                            size="sm"
                            variant="flat"
                            color={product.role === 'Assembly' ? 'success' : 'warning'}
                            className="text-[10px]"
                          >
                            {product.role}
                          </Chip>
                        </td>
                        <td className="px-3 py-2 text-center font-mono" style={{ color: 'var(--text-muted)' }}>
                          {product.width && product.height && product.depth
                            ? `${product.width}" × ${product.height}" × ${product.depth}"`
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          {isSelected ? (
                            <input
                              type="number"
                              value={selected.get(product.id) ?? 1}
                              onChange={(e) => setQuantity(product.id, parseInt(e.target.value) || 1)}
                              min={1}
                              className="w-[50px] text-center text-xs px-1 py-0.5 rounded outline-none"
                              style={{
                                backgroundColor: '#2a3a4a',
                                color: 'var(--text-primary)',
                                border: '1px solid var(--border)',
                              }}
                            />
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose}>Cancel</Button>
          <Button
            color="primary"
            onPress={handleAdd}
            isLoading={isAdding}
            isDisabled={selected.size === 0}
          >
            Add {selected.size > 0 ? `${selected.size} Product${selected.size > 1 ? 's' : ''}` : 'Selected'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
